import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { checkDonor, createDonor, createDonation } from "../api/donationApi";
import { fetchLeaderboard } from "../api/leaderboardApi";
import { FaStar, FaEnvelope, FaUsers } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css/pagination";
import "swiper/css";
import "swiper/css/autoplay";
import { Autoplay, Pagination } from "swiper/modules";

const Donate = () => {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [step, setStep] = useState(1);
  const [donor, setDonor] = useState({
    name: "",
    dob: "",
    pincode: "",
    category: "",
    consent: false,
  });
  const [amount, setAmount] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [donorsList, setDonorsList] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (showPopup) {
      const timer = setTimeout(() => {
        navigate("/");     // ⭐ homepage route
      }, 2500);            // wait 2.5 sec

      return () => clearTimeout(timer);
    }
  }, [showPopup, navigate]);



  // Fetch donors for moving stripes
  useEffect(() => {
    fetchLeaderboard().then((res) => setDonorsList(res.data));
  }, []);

  const handleMobileSubmit = async () => {
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      setErrors({ mobile: "Enter valid Indian mobile number" });
      return;
    }


    const res = await checkDonor(mobile);

    if (res.data.exists) {

      const d = res.data.donor;   // ⭐ extract once

      setDonor({
        name: d.name || "",
        dob: d.dob || "",
        pincode: d.pincode || "",
        category: "",
        consent: d.consent || false
      });

      // Go to Step 3
      setErrors({});
      setStep(3);

    } else {
      setStep(2);
    }
  };


  const handleCreateDonor = async () => {
    const newErrors = {};

    if (!donor.name) newErrors.name = "Name required";
    if (!donor.dob) newErrors.dob = "DOB required";
    if (!donor.pincode || !/^[1-9][0-9]{5}$/.test(donor.pincode))
      newErrors.pincode = "Valid pincode required";
    if (!donor.category)
      newErrors.category = "Select category";

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    await createDonor({
      mobile,
      name: donor.name,
      dob: donor.dob,
      pincode: donor.pincode,
      category: donor.category,
      consent: donor.consent,
    });
    setStep(3);
  };

  const handleDonation = async () => {
    const newErrors = {};

    if (!donor.category)
      newErrors.category = "Select category";

    if (!amount || amount <= 0)
      newErrors.amount = "Enter valid donation amount";

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    try {

      // ✅ CREATE payload first
      const payload = {
        mobile,
        amount: Number(amount),
        pincode: donor.pincode,
        donationCategory: donor.category
      };

      // ✅ DEBUG LOG
      console.log("DONATION PAYLOAD →", payload);

      // ✅ SEND
      await createDonation(payload);

      setStep(4);

    } catch (err) {
      console.log("FULL ERROR:", err);
      console.log("SERVER:", err.response?.data);
      alert(err.response?.data?.message || "Donation failed");
    }
  };

  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(
        location.hash.replace("#", "")
      );
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);


  const StepIndicator = () => (
  <div className="flex items-center justify-center mb-10 gap-6 sm:gap-8 font-medium">
    {["Mobile", "Details", "Amount", "Payment"].map((label, i) => {
      const isActive = step >= i + 1;
      const isCurrent = step === i + 1;

      return (
        <div key={i} className="flex flex-col items-center relative">
          
          {/* Circle */}
          <div
            className={`w-9 h-9 flex items-center justify-center rounded-full text-sm transition-all duration-300
            ${
              isActive
                ? "bg-gradient-to-r from-rose-600 to-rose-500 text-white shadow-lg shadow-rose-300/40"
                : "bg-gray-200 text-gray-500"
            }`}
          >
            {i + 1}
          </div>

          {/* Mobile: Show only current step label */}
          {isCurrent && (
            <span className="mt-2 text-xs text-rose-600 sm:hidden font-semibold">
              {label}
            </span>
          )}

          {/* Desktop: Always show label */}
          <span
            className={`hidden sm:block mt-2 text-sm ${
              isActive ? "text-rose-600" : "text-gray-400"
            }`}
          >
            {label}
          </span>
        </div>
      );
    })}
  </div>
);

  const importanceCards = [
    {
      title: "Srimad Bhagavatam 10.11.11",
      verse: "फलिवकियणी तस चुतधानकरदयम् । फलैरपूरयद्रतै: फलभाणमपूिर च ॥",
      desc: "While Kṛṣṇa was going to the fruit vendor very hastily, most of the grains He was holding fell. Nonetheless, the fruit vendor filled Kṛṣṇa’s hands with fruits, and her fruit basket was immediately filled with jewels and gold.",
      image: "/images/donation/importance1.webp"
    },
    {
      title: "Srimad Bhagavatam 11.27.51",
      verse: "पूजादीनां प्रवाहार्थं महापर्वस्वथान्वहम् । क्षेत्रापणपुरग्रामान् दत्त्वा मत्सार्ष्टितामियात् ॥",
      desc: "One who offers the Deity gifts of land, markets, cities and villages so that the regular daily worship and special festivals of the Deity may go on continually will achieve opulence equal to My own.",
      image: "/images/donation/importance2.jpg"
    },
    {
      title: "Bhagavad Gita 2.40",
      verse: "नेहाभिक्रमनाशोऽस्ति प्रत्यवायो न विद्यते । स्वल्पमप्यस्य धर्मस्य त्रायते महतो भयात् ॥",
      desc: "In this endeavour of offering service to Lord Kṛṣṇa, you get eternal credit, and a little service offer to Lord Kṛṣṇa can protect one from the most dangerous type of fear.",
      image: "/images/donation/importance3.jpg"
    },
    {
      title: "Srimad Bhagavatam 10.22.35",
      verse: "एतावज्जन्मसाफल्यं देहिनामिह देहिषु । प्राणैरर्थैर्धिया वाचा श्रेयआचरणं सदा ॥",
      desc: "It is the duty of every living being to perform welfare activities for the benefit of others with his life, wealth, intelligence and words.",
      image: "/images/donation/importance4.jpg"
    },
    // Add more cards here...
  ];

  return (
    <div className="min-h-screen bg-rose-50 py-12 px-4 md:px-8">
      <h1 className="text-4xl md:text-5xl font-bold text-center text-[#9A134C] mb-4 mt-8">
        Contribute
      </h1>
      <p className="text-center text-gray-700 text-lg md:text-xl mb-12 max-w-3xl mx-auto">
        Every contribution strengthens spiritual service and community welfare efforts.
      </p>

      {/* Main Donation Section */}
      <div className="max-w-4xl mx-auto bg-white shadow-2xl rounded-3xl p-8 flex justify-center">
        <div className="w-full md:w-3/4 lg:w-2/3">
          <StepIndicator />

          {/* STEP 1 */}
          {step === 1 && (
            <div className="space-y-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Mobile Number: <span className="text-red-500">*</span>
              </label>
              <input
                className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500 outline-none transition"
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value);
                  setErrors({ ...errors, mobile: null });
                }
                }
                placeholder="Enter 10-digit mobile number"
              />
              {errors.mobile && (
                <p className="text-red-500 text-sm mt-1">{errors.mobile}</p>
              )}
              <button
                onClick={handleMobileSubmit}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white py-3 rounded-lg font-semibold transition"
              >
                Continue
              </button>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-gray-700 mb-2">
                Your Details
              </h2>

              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-rose-500"
                  value={donor.name}
                  onChange={(e) => {
                    setDonor({ ...donor, name: e.target.value });
                    setErrors({ ...errors, name: null });
                  }}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              {/* DOB */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Date of Birth <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-rose-500"
                  value={donor.dob}
                  onChange={(e) => {
                    setDonor({ ...donor, dob: e.target.value });
                    setErrors({ ...errors, dob: null });
                  }}
                />
                {errors.dob && (
                  <p className="text-red-500 text-sm mt-1">{errors.dob}</p>
                )}
              </div>

              {/* Pincode */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Pincode <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-rose-500"
                  value={donor.pincode}
                  onChange={(e) => {
                    setDonor({ ...donor, pincode: e.target.value });
                    setErrors({ ...errors, pincode: null });
                  }}
                />

                {errors.pincode && (
                  <p className="text-red-500 text-sm mt-1">{errors.pincode}</p>
                )}
              </div>

              {/* Donation Category */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Donation Category <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-rose-500 bg-white"
                  value={donor.category}
                  onChange={(e) => {
                    setDonor({ ...donor, category: e.target.value });
                    setErrors({ ...errors, category: null });
                  }}
                >
                  <option value="">Select Category</option>
                <option>General Donation</option>
                <option>Gau Seva</option>
                <option>Mandir Nirman Seva</option>
                <option>Vigraha Seva</option>
                <option>Nitya Seva</option>
                <option>Annadan Seva</option>
                <option>Sadhu Seva</option>
                <option>Festival Seva</option>
                <option>Ekadashi Seva</option>
                </select>
                {errors.category && (
                  <p className="text-red-500 text-sm mt-1">{errors.category}</p>
                )}
              </div>


              <button
                onClick={handleCreateDonor}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white py-3 rounded-lg font-semibold transition shadow-md"
              >
                Proceed to Donate
              </button>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-700 ">
                Select Donation Category
              </h2>

              <select
                disabled={step === 3 && donor.category}
                className="w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-rose-500 bg-white"
                value={donor.category}
                onChange={(e) => { setDonor({ ...donor, category: e.target.value }); setErrors({ ...errors, category: null }); }
                }
              >
                <option value="">Select Category</option>
                <option>General Donation</option>
                <option>Gau Seva</option>
                <option>Mandir Nirman Seva</option>
                <option>Vigraha Seva</option>
                <option>Nitya Seva</option>
                <option>Annadan Seva</option>
                <option>Sadhu Seva</option>
                <option>Festival Seva</option>
                <option>Ekadashi Seva</option>
              </select>
              <h2 className="text-xl font-semibold text-gray-700">
                Select Donation Amount
              </h2>

              <div className="grid grid-cols-3 gap-4">
                {[1100, 2100, 5100, 7100].map((amt) => (
                  <button
                    key={amt}
                    onClick={() => setAmount(Number(amt))}
                    className={`p-4 rounded-xl border text-lg font-semibold transition
                    ${amount === amt
                        ? "bg-rose-600 text-white border-rose-600"
                        : "bg-white hover:bg-rose-50"
                      }`}
                  >
                    ₹{amt}
                  </button>
                ))}
              </div>

              <input
                type="number"
                placeholder="Or enter custom amount"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-rose-400"
              />
              {errors.amount && (
                <p className="text-red-500 text-sm">{errors.amount}</p>
              )}

              {/* Continue Button */}
              {amount > 0 && (
                <button
                  onClick={handleDonation}
                  className="w-full bg-rose-600 hover:bg-rose-700 text-white py-3 rounded-lg font-bold text-lg transition shadow-md"
                >
                  Continue to Payment
                </button>
              )}
            </div>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <div className="space-y-6 text-center">

              <h2 className="text-2xl font-bold text-[#9A134C]">
                Complete Your Payment
              </h2>

              <p className="text-lg font-semibold text-gray-800">
                Donation Amount: ₹{amount}
              </p>

              <p className="text-gray-600">
                Please scan the QR code using your mobile payment app and complete the payment.
              </p>

              {/* QR IMAGE */}
              <div className="flex justify-center">
                <img
                  src="/qr.jpg"
                  alt="UPI QR Code"
                  className="w-64 h-64 object-contain rounded-2xl shadow-lg border"
                />
              </div>

              {/* UPI ID */}
              <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                <p className="text-gray-700 font-medium">
                  Or use UPI ID:
                </p>
                <p className="text-lg font-bold text-[#9A134C] mt-1">
                  iskcon.123@icici
                </p>
              </div>

              {/* 🏦 Bank Transfer Details */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                <h3 className="text-lg font-semibold text-[#9A134C] mb-3">
                  Bank Transfer Details
                </h3>

                <div className="grid grid-cols-2 gap-y-2 text-sm text-gray-700">
                  <span className="font-semibold">Account Name:</span>
                  <span>ISKCON</span>

                  <span className="font-semibold">A/C No:</span>
                  <span>001201037882</span>

                  <span className="font-semibold">IFSC Code:</span>
                  <span>ICIC0000550</span>

                  <span className="font-semibold">Bank:</span>
                  <span>ICICI Bank</span>
                </div>
              </div>

              {/* Instructions */}
              <div className="text-left bg-gray-50 p-4 rounded-xl border space-y-2 text-gray-700">
                <p>• Scan the QR from your mobile payment app and pay.</p>
                <p>• Or transfer using the UPI ID above.</p>
                <p>• Keep the payment screenshot saved.</p>
                <p>• Our volunteer will contact you soon for confirmation.</p>
              </div>

              {/* I HAVE PAID BUTTON */}
              <button
                onClick={() => setShowPopup(true)}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-bold text-lg transition shadow-md"
              >
                I HAVE PAID
              </button>

              {/* Back Button */}
              <button
                onClick={() => setStep(3)}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Go Back
              </button>

            </div>
          )}
        </div>
      </div>

      {/* Moving Donors Stripes */}
      <section 
	  id="donors"
	  className="mt-12 overflow-hidden text-center bg-rose-50">
        <h2 className="text-3xl font-bold text-[#9A134C] mb-6 my-8">Respected Donors</h2>
        <div className="flex flex-col gap-4">
          {[0, 1, 2].map((i) => (
            <div className="overflow-hidden">
              <div className={`marquee-track ${i % 2 === 0 ? "animate-marquee" : "animate-marquee-reverse"}`}>
                {[...donorsList, ...donorsList].map((d, idx) => (
                  <div
                    key={idx}
                    className="bg-rose-100 text-rose-700 px-4 py-2 rounded-lg min-w-max font-medium shadow"
                  >
                    {d.name}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Donor Privileges */}
      <section className="max-w-6xl mx-auto mt-16 px-4 text-center">
        <h2 className="text-3xl font-bold text-[#9A134C] mb-12">Donor Privileges</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            {
              text: "Receive Maha Prasadam",
              desc: "Sanctified Maha Prasadam sent from Sri Sri Giridhari Dauji Mandir to your home - a divine gift of the Lord’s mercy.",
              icon: <FaStar size={28} />,
              bg: "bg-gradient-to-tr from-pink-200 to-pink-400",
              iconBg: "bg-pink-400/20 text-pink-700",
            },
            {
              text: "Special Gift of Spiritual Books",
              desc: "Enrich your spiritual life with selected books full of timeless wisdom and bhakti.",
              icon: <FaUsers size={28} />,
              bg: "bg-gradient-to-tr from-purple-200 to-purple-400",
              iconBg: "bg-purple-400/20 text-purple-700",
            },
            {
              text: "80G Tax Benefit Certificate",
              desc: "Receive tax benefits under Section 80G of the Income Tax Act on your valuable contribution.",
              icon: <FaEnvelope size={28} />,
              bg: "bg-gradient-to-tr from-teal-200 to-teal-400",
              iconBg: "bg-teal-400/20 text-teal-700",
            },
            {
              text: "Digital Certificate of Contribution",
              desc: "A beautiful, personalized digital certificate recognizing your generous support.",
              icon: <FaStar size={28} />,
              bg: "bg-gradient-to-tr from-yellow-200 to-yellow-400",
              iconBg: "bg-yellow-400/20 text-yellow-700",
            },
          ].map((priv, i) => (
            <div
              key={i}
              className={`relative ${priv.bg} rounded-3xl shadow-lg p-8 flex flex-col items-center gap-4 transform transition duration-500 hover:-translate-y-3 hover:scale-105 hover:shadow-2xl`}
            >
              <div
                className={`w-16 h-16 flex items-center justify-center rounded-full ${priv.iconBg} mb-2`}
              >
                {priv.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{priv.text}</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{priv.desc}</p>
              {/* Decorative circles for depth */}
              <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-white/20 blur-2xl"></div>
              <div className="absolute -bottom-4 -right-4 w-14 h-14 rounded-full bg-white/10 blur-3xl"></div>
            </div>
          ))}
        </div>
      </section>

      {/* Importance of Donation - Full Text Slider */}
      <section className="max-w-6xl mx-auto mt-16 px-4 text-center">
        <h2 className="text-3xl font-bold text-[#9A134C] mb-12">Importance Of Donation</h2>

        <Swiper
          modules={[Autoplay, Pagination]} // Added Pagination module
          spaceBetween={20}
          slidesPerView={1.2}
          breakpoints={{
            640: { slidesPerView: 1.5 },
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
          loop={true}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          pagination={{ clickable: true }} // <-- This adds swipe dots
        >
          {importanceCards.map((card, i) => (
            <SwiperSlide key={i} className="!h-auto min-h-[420px]">
              <div className="overflow-hidden rounded-xl bg-white h-full shadow-lg flex flex-col">
                {/* Image placeholder */}
                <div className="w-full h-64 bg-gray-100 flex items-center justify-center text-gray-400 text-lg">
                  <img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-full object-cover rounded-t-xl"
                  />
                </div>
                {/* Text */}
                <div className="space-y-4 p-4 w-full">
                  <p className="text-primary font-semibold text-center text-base">{card.title}</p>
                  <div className="font-semibold text-sm text-gray-600 text-center italic">
                    <p>{card.verse}</p>
                  </div>
                  <p className="text-justify text-gray-700">{card.desc}</p>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      {/* Thank You Popup */}
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-md text-center border border-rose-100">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-rose-100 flex items-center justify-center text-rose-600 text-2xl">
              ✓
            </div>
            <h2 className="text-2xl font-bold text-rose-700 mb-3">Thank You for Your Seva</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              Your offering has been recorded. Our volunteer will contact you shortly to collect the payment screenshot for verification.
            </p>
            <p className="text-xs text-gray-400 mt-4">Redirecting to home page...</p>
          </div>
        </div>
      )}

      <style>
        {`
        /* Full continuous marquee */
.marquee-track {
  display: inline-flex;        /* align items horizontally */
  gap: 1rem;
  min-width: max-content;      /* ensures the track stretches to fit content */
}

@keyframes marquee {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); } /* scroll half since content is duplicated */
}

@keyframes marquee-reverse {
  0% { transform: translateX(-50%); }
  100% { transform: translateX(0); }
}

.animate-marquee {
  animation: marquee 50s linear infinite;
}

.animate-marquee-reverse {
  animation: marquee-reverse 50s linear infinite;
}

        `}
      </style>
    </div>
  );
};

export default Donate;

